import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import {
  format,
  isToday,
  isTomorrow,
  isSameDay,
  parseISO,
  addDays
} from 'date-fns';
import styles from './bookingList.module.scss';
import {
  KoButton,
  KoDescription,
  KoPopOver,
  KoStaicMembers,
  KoTag,
  KoText,
  KoTitle
} from 'packages';
import KoIcon from '@kwapio/icons';
import { Link } from 'react-router-dom';

const groupByDateBucket = (bookings) => {
  const groups = {
    Today: [],
    Tomorrow: [],
    'Day After Tomorrow': [],
    'Rest of This Month': {}
  };

  const today = new Date();

  bookings.forEach((booking) => {
    const bookingDate = parseISO(booking.date);

    if (isToday(bookingDate)) {
      groups.Today.push(booking);
    } else if (isTomorrow(bookingDate)) {
      groups.Tomorrow.push(booking);
    } else if (isSameDay(bookingDate, addDays(today, 2))) {
      groups['Day After Tomorrow'].push(booking);
    } else if (
      bookingDate > addDays(today, 2) &&
      bookingDate.getMonth() === today.getMonth() &&
      bookingDate.getFullYear() === today.getFullYear()
    ) {
      const label = format(bookingDate, 'EEE, MMM d');
      if (!groups['Rest of This Month'][label]) {
        groups['Rest of This Month'][label] = [];
      }
      groups['Rest of This Month'][label].push(booking);
    }
  });

  return groups;
};

const normalizeSlot = (slot) => {
  if (!slot || typeof slot !== 'string') {
    return { start: '00:00', end: '00:00' };
  }

  try {
    const regex = /start:(\d{1,2}:\d{2})\s*,\s*end:(\d{1,2}:\d{2})/i;
    const match = slot.match(regex);
    if (match) {
      return {
        start: match[1],
        end: match[2]
      };
    }
    return { start: '00:00', end: '00:00' };
  } catch {
    return { start: '00:00', end: '00:00' };
  }
};

const formatTime12h = (date) => {
  if (isNaN(date.getTime())) return '00:00 AM';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

const BookingListByDate = ({ bookings, currentEvent, currentdate }) => {
  const formattedDate = dayjs(currentdate).format('YYYY-MM-DD');
  console.log('currentEvent', currentEvent);
  useEffect(() => {
    if (formattedDate) {
      setTimeout(() => {
        const section = document.getElementById(`section-${formattedDate}`);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100); // wait for DOM to render
    }
  }, [formattedDate, bookings]);

  if (!bookings?.length) return null;

  const grouped = groupByDateBucket(bookings);

  const getSlotLabel = (booking) => {
    const { start, end } = normalizeSlot(booking.slot);
    const slotDate = parseISO(booking.date);

    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);

    const startDateTime = new Date(slotDate);
    const endDateTime = new Date(slotDate);
    startDateTime.setHours(startH, startM ?? 0, 0, 0);
    endDateTime.setHours(endH, endM ?? 0, 0, 0);

    return `${formatTime12h(startDateTime)}`;
  };


  const content = (eventObj) => {
    return (
      eventObj && <div className={styles.eventContent}>
        <div className={styles.eventAssignees}>
          <KoTitle  text={'Event Organizer'} level={5} />
          <div className={styles.eventAssignee}>
            <KoStaicMembers data={currentEvent.organizer} />
          </div>
          <KoTitle className={styles.eventNameTitile} text={'Organizing Team'} level={5} />
          <div className={styles.eventAssignee}>
            {currentEvent.assignees?.map((assignee, index) => (
              <div key={index}>
                <KoStaicMembers data={assignee} />
              </div>
            ))}
          </div>
          <KoTitle className={styles.eventNameTitile} text={'Booked By'} level={5} />
          <div className={styles.bookedBy}>
            {eventObj.email}
          </div>
          {eventObj?.guestEmail && <KoTitle className={styles.eventNameTitile} text={'Guest List'} level={5} />}
          {eventObj?.guestEmail && <div className={styles.eventAssignee}>
            <div className={styles.bookedBy}>
              {eventObj?.guestEmail?.join(', ')}
            </div>
          </div>}
        </div>

      </div>
    );
  };

  const getFormattedDuration = (startTime, endTime) => {
    const start = dayjs.utc(startTime).local();
    const end = dayjs.utc(endTime).local();
  
    const duration = end.diff(start, 'minute');
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
  
    return `${start.format('hh:mm')} to ${end.format('hh:mm A')} (${hours}h${minutes ? ` ${minutes}m` : ''})`;
  };

  const moreinfo = (eventObj, isJoinable) => {
    return (
      eventObj && <div className={styles.eventContent}>
        <KoText className={styles.eventTime} type="secondary" text={getFormattedDuration(eventObj.startTime, eventObj.endTime)} />
      
        <KoDescription
          className={styles.description}
          ellipsis={true}
          text={eventObj.notes || 'Notes not available'}
        />
        <KoText
          className={styles.bookerDate}
          text={`Booked on: ${format(parseISO(eventObj.date), 'PP')}`}
        />
        <div className={styles.bookedBymore}>
          Booked By: {eventObj.email}
        </div>
        <div className={styles.link}>
          <Link href={`${eventObj.url}`} target="_blank">
            <KoText text="Join Now / Copy Link" copyable={{ text: eventObj.url}} />
          </Link>
        </div>
        { isJoinable && (
          <KoButton text="Join" onClick={() => eventObj.url} />
        )}
      </div>
    );
  };

  const renderBookings = (list = []) => {
    return list
      .slice()
      .sort((a, b) => {
        const { start: startA } = normalizeSlot(a.slot);
        const { start: startB } = normalizeSlot(b.slot);
        const [hourA, minA] = startA.split(':').map(Number);
        const [hourB, minB] = startB.split(':').map(Number);
        return hourA * 60 + minA - (hourB * 60 + minB);
      })
      .map((b) => {
        const { start, end } = normalizeSlot(b.slot);
        const slotDate = parseISO(b.date);
        const now = new Date();

        const [startH, startM] = start.split(':').map(Number);
        const [endH, endM] = end.split(':').map(Number);

        const startDateTime = new Date(slotDate);
        const endDateTime = new Date(slotDate);
        startDateTime.setHours(startH, startM ?? 0, 0, 0);
        endDateTime.setHours(endH, endM ?? 0, 0, 0);

        const isSameDayCheck = (d1, d2) =>
          d1.getFullYear() === d2.getFullYear() &&
          d1.getMonth() === d2.getMonth() &&
          d1.getDate() === d2.getDate();

        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const isPastDay = slotDate < today;

        const isCompleted =
          isPastDay ||
          (isSameDayCheck(slotDate, today) && now > endDateTime);

        const isJoinable =
          isSameDayCheck(slotDate, today) &&
          now >= startDateTime &&
          now <= endDateTime;

        return (
          <div key={b.bookingId} className={styles.slotCard}>
            <div className={styles.bookerTime}>
              <KoIcon name="LogOutlined" width={14} height={14} />
              {getSlotLabel(b)}
              <KoPopOver
                content={moreinfo(b, isJoinable)}
                placement="topRight"
              >
                <div className={styles.open}>
                  <KoIcon name="OpenOutlined" width={14} height={14} />
                </div>
              </KoPopOver>
            </div>
            <div className={styles.bookerInfo}>
              <KoText
                ellipsis={true}
                className={styles.bookerName}
                text={b.name}
              />
            </div>
            <KoText
              className={styles.bookerDate}
              text={`Booked on: ${format(parseISO(b.date), 'PP')}`}
            />
            
            <div className={styles.bookerStatus}>
              <KoPopOver
                content={content(b)}
                placement="topRight"
              >
                <div className={styles.bookerEmail}>
                  <KoIcon name="UsersOutlined" width={14} height={14} />
                </div>
              </KoPopOver>
            
              {isCompleted ? (
                <KoTag value="Completed" color="green" />
              ) : isJoinable && (
                <KoButton text="Join" onClick={() => b.url} />
              )}
            </div>
          </div>
        );
      });
  };

  return (
    <div className={styles.wrapper}>
      {['Today', 'Tomorrow', 'Day After Tomorrow'].map((label) => {
        const offsetDays =
          label === 'Tomorrow' ? 1 : label === 'Day After Tomorrow' ? 2 : 0;
        const dateId = format(addDays(new Date(), offsetDays), 'yyyy-MM-dd');

        return (
          grouped[label]?.length > 0 && (
            <div key={label} className={styles.section} id={`section-${dateId}`}>
              <KoTitle
                text={`${label} – ${grouped[label].length} ${grouped[label].length > 1 ? 'Slots' : 'Slot'
                  }`}
                level={4}
                className={styles.title}
              />
              <div className={styles.bookings}>
                {renderBookings(grouped[label])}
              </div>
            </div>
          )
        );
      })}

      {Object.entries(grouped['Rest of This Month'])
        .sort(([labelA], [labelB]) => new Date(labelA) - new Date(labelB))
        .map(([label, list]) => {
          const dateId = format(parseISO(list[0].date), 'yyyy-MM-dd');
          return (
            <div key={label} className={styles.section} id={`section-${dateId}`}>
              <KoTitle
                text={`${label} – ${list.length} ${list.length > 1 ? 'Slots' : 'Slot'
                  }`}
                level={4}
                className={styles.title}
              />
              <div className={styles.bookings}>{renderBookings(list)}</div>
            </div>
          );
        })}
    </div>
  );
};

export default BookingListByDate;
